import React, { useCallback, useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import "./CameraStream.css";

const RETRY_DELAY_MS = 5000;
const VALID_TOGGLE_POSITIONS = new Set([
  "top-left",
  "top-center",
  "top-right",
  "bottom-left",
  "bottom-center",
  "bottom-right",
]);

const CameraStream = ({
  draggable,
  url = "",
  showPowerToggle = true,
  powerTogglePosition = "bottom-right",
}) => {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const retryRef = useRef(null);
  const [status, setStatus] = useState("loading");
  const [isEnabled, setIsEnabled] = useState(true);

  const streamUrl = String(url || "").trim();
  const togglePosition = VALID_TOGGLE_POSITIONS.has(powerTogglePosition)
    ? powerTogglePosition
    : "bottom-right";
  const toggleAtTop = togglePosition.startsWith("top-");
  const toggleAlignment = togglePosition.split("-")[1];

  const stopPlayer = useCallback(() => {
    if (retryRef.current) {
      clearTimeout(retryRef.current);
      retryRef.current = null;
    }

    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    const video = videoRef.current;
    if (video) {
      video.onloadedmetadata = null;
      video.onerror = null;
      video.pause();
      video.removeAttribute("src");
      video.load();
    }
  }, []);

  const startPlayer = useCallback(() => {
    const video = videoRef.current;
    if (!video || !isEnabled || !streamUrl) return;

    stopPlayer();
    setStatus("loading");

    if (Hls.isSupported()) {
      const hls = new Hls({
        liveDurationInfinity: true,
        lowLatencyMode: true,
      });

      hlsRef.current = hls;
      hls.attachMedia(video);
      hls.loadSource(streamUrl);

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        video.play().catch(() => {});
        setStatus("online");
      });

      hls.on(Hls.Events.ERROR, (_, data) => {
        if (!data.fatal) return;

        setStatus("offline");
        hls.destroy();
        if (hlsRef.current === hls) hlsRef.current = null;

        if (!retryRef.current) {
          retryRef.current = setTimeout(() => {
            retryRef.current = null;
            startPlayer();
          }, RETRY_DELAY_MS);
        }
      });
      return;
    }

    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = streamUrl;
      video.onloadedmetadata = () => {
        video.play().catch(() => {});
        setStatus("online");
      };
      video.onerror = () => setStatus("offline");
      return;
    }

    setStatus("unsupported");
  }, [isEnabled, stopPlayer, streamUrl]);

  useEffect(() => {
    if (!isEnabled) {
      stopPlayer();
      setStatus("off");
      return undefined;
    }

    if (!streamUrl) {
      stopPlayer();
      setStatus("missing-url");
      return undefined;
    }

    startPlayer();
    return stopPlayer;
  }, [isEnabled, startPlayer, stopPlayer, streamUrl]);

  const powerToggle = showPowerToggle ? (
    <div className={`camera-stream-controls is-${toggleAlignment}`}>
      <button
        type="button"
        className={`camera-stream-power ${isEnabled ? "is-on" : "is-off"}`}
        role="switch"
        aria-checked={isEnabled}
        aria-label={isEnabled ? "Turn camera widget off" : "Turn camera widget on"}
        onClick={() => setIsEnabled((current) => !current)}
        onPointerDown={(event) => event.stopPropagation()}
        draggable={false}
      >
        <span className="camera-stream-power__track" aria-hidden="true">
          <span className="camera-stream-power__thumb" />
        </span>
        <span>{isEnabled ? "On" : "Off"}</span>
      </button>
    </div>
  ) : null;

  const statusLabel = {
    loading: "Connecting...",
    offline: "Camera Offline",
    off: "Camera widget is off",
    "missing-url": "Stream URL is not configured",
    unsupported: "This browser cannot play the stream",
  }[status];

  return (
    <div
      className={`widget-container camera-stream-widget ${draggable ? "is-draggable" : "not-draggable"}`}
      draggable={draggable}
      onDragStart={(event) => {
        const id = event.target.closest(".widget")?.id;
        if (!id) return;

        const rect = event.target.getBoundingClientRect();
        const offsetX = event.clientX - rect.x;
        const offsetY = event.clientY - rect.y;
        const action = id.startsWith("base") ? "add" : "move";
        event.dataTransfer.setData("text/plain", `camera-stream;${id};${offsetX};${offsetY};${action}`);
      }}
    >
      {toggleAtTop && powerToggle}
      <div className="camera-stream-container">
        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          controls={false}
          className="camera-stream-video"
        />

        {status !== "online" && (
          <div className="camera-stream-overlay" role="status" aria-live="polite">
            {statusLabel}
          </div>
        )}
      </div>
      {!toggleAtTop && powerToggle}
    </div>
  );
};

export default CameraStream;
